import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button, Popover, Box } from "@shopify/polaris";
import { ClockIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { AuditLogList } from "./audit-log-list";

type Props = {
  companyId?: string;
  subjectType?: string;
  subjectId?: string;
  label?: string;
};

/**
 * A reusable Popover component that displays audit logs for a given subject.
 * Perfect for page headers or small spaces.
 */
export function AuditLogPopover({ 
  companyId: companyIdProps, 
  subjectType, 
  subjectId,
  label 
}: Props) {
  const { companyId: companyIdParams } = useParams();
  const companyId = companyIdProps || companyIdParams || "";
  const { t } = useTranslation();
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    [],
  );

  const activator = (
    <Button
      onClick={togglePopoverActive}
      icon={<ClockIcon />}
      variant="tertiary"
    >
      {label ?? t("activity_log", { defaultValue: "History" })}
    </Button>
  );

  return (
    <div className="relative inline-flex shrink-0">
      <Popover
        active={popoverActive}
        activator={activator}
        autofocusTarget="first-node"
        onClose={togglePopoverActive}
        sectioned={false}
      >
        {popoverActive ? (
          // Polaris caps the popover at 25rem and at the screen width minus 2rem; a wider box is clipped, not shrunk.
          <Box width="min(25rem, calc(100vw - 2rem))" padding="300">
            <AuditLogList
              companyId={companyId}
              subjectType={subjectType}
              subjectId={subjectId}
              maxHeight="500px"
              pageSize={10}
            />
          </Box>
        ) : null}
      </Popover>
    </div>
  );
}
